import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { dispatchSnackbarWarningEvent } from '@dreryk/react-components';

import { useVisitTranscriptSummary } from '../transcription/useVisitTranscriptSummary';
import { translationKeys } from '../translations';
import type { AiAssistantMainMenuTileItemSummaryModalOpenCallback } from '../mainMenuTileItem/AiAssistantMainMenuTileItemSummaryModal.types';
import { buildSummaryUtterances } from '../transcription/utterances';

import { useAiAssistantTranscriptionContext } from './AiAssistantTranscription.context';
import { useAiAssistantRecordingGlobalState } from './useAiAssistantRecordingGlobalState';

type AiAssistantVisitSummaryContextValue = {
   summarizeTranscript: VoidFunction;
   summarizeMobileTranscript: (transcript: string) => void;
};

const MOBILE_TRANSCRIPT_SPEAKER = 'spk_0';

const aiAssistantVisitSummaryContext = createContext<AiAssistantVisitSummaryContextValue | null>(null);

export const useAiAssistantVisitSummaryContext = (): AiAssistantVisitSummaryContextValue => {
   const context = useContext(aiAssistantVisitSummaryContext);

   if (context === null) {
      throw new Error('useAiAssistantVisitSummaryContext cannot be used outside AiAssistantVisitSummaryProvider');
   }
   return context;
};

const { Provider } = aiAssistantVisitSummaryContext;

type AiAssistantVisitSummaryProviderProps = {
   children: JSX.Element;
   onSummarySuccess: AiAssistantMainMenuTileItemSummaryModalOpenCallback;
};

export const AiAssistantVisitSummaryProvider = (props: AiAssistantVisitSummaryProviderProps): JSX.Element => {
   const { getTranscriptUtterances, clearTranscript } = useAiAssistantTranscriptionContext();
   const { state, reset, completeGeneration, failGeneration } = useAiAssistantRecordingGlobalState();
   const stateRef = useRef(state);
   stateRef.current = state;

   const { execute, error } = useVisitTranscriptSummary({
      onSuccess: (result) => {
         const currentState = stateRef.current;

         if (currentState.type !== 'pending' && currentState.type !== 'mobilePending') {
            return;
         }

         clearTranscript();
         completeGeneration();
         props.onSummarySuccess(result ?? null, currentState.accumulatedSeconds);
      },
   });

   useEffect(() => {
      if (error) {
         failGeneration();
      }
   }, [error, failGeneration]);

   const summarizeTranscript = useCallback(() => {
      const utterances = buildSummaryUtterances(getTranscriptUtterances());

      if (utterances.length === 0) {
         clearTranscript();
         reset();
         dispatchSnackbarWarningEvent(translationKeys.mainMenuTileItem.emptyTranscriptWarning);
         return;
      }

      execute({ utterances: utterances });
   }, [getTranscriptUtterances, clearTranscript, reset, execute]);

   const summarizeMobileTranscript = useCallback(
      (transcript: string) => {
         execute({ utterances: [{ speaker: MOBILE_TRANSCRIPT_SPEAKER, text: transcript }] });
      },
      [execute]
   );

   const value = useMemo(
      () => ({ summarizeTranscript: summarizeTranscript, summarizeMobileTranscript: summarizeMobileTranscript }),
      [summarizeTranscript, summarizeMobileTranscript]
   );

   return <Provider value={value}>{props.children}</Provider>;
};
